// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/**
 * UAR Professional Theme
 *
 * Enterprise-grade color palette and design system for the
 * Database Access Management & Attestation (UAR) compliance application.
 *
 * Based on professional compliance application standards with
 * emphasis on readability, accessibility, and trustworthiness.
 */

:root {
  /* ===== PRIMARY COLORS ===== */
  /* Professional blues for trust and reliability */
  --primary-50: #e3f2fd;
  --primary-100: #bbdefb;
  --primary-200: #90caf9;
  --primary-300: #64b5f6;
  --primary-400: #42a5f5;
  --primary-500: #2196f3;  /* Main brand color */
  --primary-600: #1e88e5;
  --primary-700: #1976d2;
  --primary-800: #1565c0;
  --primary-900: #0d47a1;

  /* ===== SECONDARY COLORS ===== */
  /* Complementary teal for accents */
  --secondary-50: #e0f2f1;
  --secondary-100: #b2dfdb;
  --secondary-200: #80cbc4;
  --secondary-300: #4db6ac;
  --secondary-400: #26a69a;
  --secondary-500: #009688;  /* Secondary brand color */
  --secondary-600: #00897b;
  --secondary-700: #00796b;
  --secondary-800: #00695c;
  --secondary-900: #004d40;

  /* ===== NEUTRAL COLORS ===== */
  /* Grays for backgrounds, borders, and text */
  --gray-50: #fafafa;
  --gray-100: #f5f5f5;
  --gray-200: #eeeeee;
  --gray-300: #e0e0e0;
  --gray-400: #bdbdbd;
  --gray-500: #9e9e9e;
  --gray-600: #757575;
  --gray-700: #616161;
  --gray-800: #424242;
  --gray-900: #212121;

  /* ===== STATUS COLORS ===== */
  /* Semantic colors for user feedback */
  --success-light: #c8e6c9;
  --success: #4caf50;
  --success-dark: #388e3c;

  --warning-light: #fff9c4;
  --warning: #ffc107;
  --warning-dark: #f57f17;

  --danger-light: #ffcdd2;
  --danger: #f44336;
  --danger-dark: #c62828;

  --info-light: #b3e5fc;
  --info: #03a9f4;
  --info-dark: #0277bd;

  /* ===== PRIVILEGE LEVEL COLORS ===== */
  /* Specific to UAR privilege categorization */
  --privilege-dba: #d32f2f;           /* DBA - Red */
  --privilege-elevated: #f57c00;      /* Elevated - Orange */
  --privilege-developer: #fbc02d;     /* Developer - Amber */
  --privilege-business: #388e3c;      /* Business - Green */
  --privilege-readonly: #1976d2;      /* Read-Only - Blue */
  --privilege-limited: #7b1fa2;       /* Limited - Purple */
  --privilege-none: #616161;          /* None/Login - Gray */

  /* ===== BACKGROUND COLORS ===== */
  --background-primary: #ffffff;
  --background-secondary: #f8f9fa;
  --background-tertiary: #f1f3f5;
  --background-hover: #e9ecef;
  --background-selected: #e3f2fd;
  --background-dark: #263238;
  --background-darker: #1a1f24;

  /* ===== TEXT COLORS ===== */
  --text-primary: #212121;
  --text-secondary: #616161;
  --text-tertiary: #9e9e9e;
  --text-disabled: #bdbdbd;
  --text-inverse: #ffffff;
  --text-link: #1976d2;
  --text-link-hover: #1565c0;

  /* ===== BORDER COLORS ===== */
  --border-light: #e0e0e0;
  --border-medium: #bdbdbd;
  --border-dark: #757575;
  --border-focus: #2196f3;
  --border-error: #f44336;

  /* ===== SPACING SYSTEM ===== */
  --space-xs: 0.25rem;   /* 4px */
  --space-sm: 0.5rem;    /* 8px */
  --space-md: 1rem;      /* 16px */
  --space-lg: 1.5rem;    /* 24px */
  --space-xl: 2rem;      /* 32px */
  --space-2xl: 3rem;     /* 48px */
  --space-3xl: 4rem;     /* 64px */

  /* ===== TYPOGRAPHY ===== */
  --font-family-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
                      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  --font-family-mono: 'Courier New', Courier, monospace;

  --font-size-xs: 0.75rem;     /* 12px */
  --font-size-sm: 0.875rem;    /* 14px */
  --font-size-base: 1rem;      /* 16px */
  --font-size-lg: 1.125rem;    /* 18px */
  --font-size-xl: 1.25rem;     /* 20px */
  --font-size-2xl: 1.5rem;     /* 24px */
  --font-size-3xl: 1.875rem;   /* 30px */
  --font-size-4xl: 2.25rem;    /* 36px */

  --font-weight-light: 300;
  --font-weight-normal: 400;
  --font-weight-medium: 500;
  --font-weight-semibold: 600;
  --font-weight-bold: 700;

  --line-height-tight: 1.25;
  --line-height-normal: 1.5;
  --line-height-relaxed: 1.75;

  /* ===== BORDER RADIUS ===== */
  --radius-sm: 0.25rem;    /* 4px */
  --radius-md: 0.375rem;   /* 6px */
  --radius-lg: 0.5rem;     /* 8px */
  --radius-xl: 0.75rem;    /* 12px */
  --radius-2xl: 1rem;      /* 16px */
  --radius-full: 9999px;   /* Circular */

  /* ===== SHADOWS ===== */
  --shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  --shadow-inner: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);

  /* ===== Z-INDEX LAYERS ===== */
  --z-dropdown: 1000;
  --z-sticky: 1020;
  --z-fixed: 1030;
  --z-modal-backdrop: 1040;
  --z-modal: 1050;
  --z-popover: 1060;
  --z-tooltip: 1070;

  /* ===== TRANSITIONS ===== */
  --transition-fast: 150ms ease-in-out;
  --transition-base: 250ms ease-in-out;
  --transition-slow: 350ms ease-in-out;

  /* ===== COMPONENT-SPECIFIC ===== */
  --header-height: 60px;
  --sidebar-width: 250px;
  --sidebar-collapsed-width: 60px;
  --footer-height: 50px;

  /* Card styles */
  --card-background: var(--background-primary);
  --card-border: var(--border-light);
  --card-shadow: var(--shadow-sm);
  --card-radius: var(--radius-lg);
  --card-padding: var(--space-lg);

  /* Button styles */
  --button-padding-y: 0.5rem;
  --button-padding-x: 1rem;
  --button-radius: var(--radius-md);
  --button-font-weight: var(--font-weight-medium);

  /* Input styles */
  --input-height: 2.5rem;
  --input-padding-x: 0.75rem;
  --input-border: var(--border-light);
  --input-border-focus: var(--border-focus);
  --input-radius: var(--radius-md);
  --input-background: var(--background-primary);

  /* Table styles */
  --table-border: var(--border-light);
  --table-stripe: var(--gray-50);
  --table-hover: var(--background-hover);
  --table-header-bg: var(--gray-100);
  --table-header-text: var(--text-primary);
}

/* ===== DARK MODE SUPPORT ===== */
/* (Optional - can be enabled later) */
@media (prefers-color-scheme: dark) {
  :root.dark-mode {
    --background-primary: #1a1f24;
    --background-secondary: #263238;
    --background-tertiary: #2e3944;
    --background-hover: #37424d;
    --background-selected: #1565c0;

    --text-primary: #e0e0e0;
    --text-secondary: #b0b0b0;
    --text-tertiary: #808080;
    --text-disabled: #606060;
    --text-inverse: #212121;

    --border-light: #37424d;
    --border-medium: #4a5660;
    --border-dark: #616e7c;

    --card-background: #263238;
    --card-border: #37424d;

    --table-stripe: #2e3944;
    --table-hover: #37424d;
    --table-header-bg: #2e3944;
  }
}

/* ===== GLOBAL RESETS ===== */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: var(--font-family-sans);
  font-size: var(--font-size-base);
  line-height: var(--line-height-normal);
  color: var(--text-primary);
  background-color: var(--background-secondary);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ===== UTILITY CLASSES ===== */

/* Text utilities */
.text-primary { color: var(--text-primary); }
.text-secondary { color: var(--text-secondary); }
.text-tertiary { color: var(--text-tertiary); }
.text-inverse { color: var(--text-inverse); }
.text-success { color: var(--success); }
.text-warning { color: var(--warning); }
.text-danger { color: var(--danger); }
.text-info { color: var(--info); }

/* Background utilities */
.bg-primary { background-color: var(--background-primary); }
.bg-secondary { background-color: var(--background-secondary); }
.bg-tertiary { background-color: var(--background-tertiary); }
.bg-success-light { background-color: var(--success-light); }
.bg-warning-light { background-color: var(--warning-light); }
.bg-danger-light { background-color: var(--danger-light); }
.bg-info-light { background-color: var(--info-light); }

/* Spacing utilities */
.m-0 { margin: 0; }
.m-xs { margin: var(--space-xs); }
.m-sm { margin: var(--space-sm); }
.m-md { margin: var(--space-md); }
.m-lg { margin: var(--space-lg); }
.m-xl { margin: var(--space-xl); }

.mt-0 { margin-top: 0; }
.mt-xs { margin-top: var(--space-xs); }
.mt-sm { margin-top: var(--space-sm); }
.mt-md { margin-top: var(--space-md); }
.mt-lg { margin-top: var(--space-lg); }
.mt-xl { margin-top: var(--space-xl); }

.mb-0 { margin-bottom: 0; }
.mb-xs { margin-bottom: var(--space-xs); }
.mb-sm { margin-bottom: var(--space-sm); }
.mb-md { margin-bottom: var(--space-md); }
.mb-lg { margin-bottom: var(--space-lg); }
.mb-xl { margin-bottom: var(--space-xl); }

.p-0 { padding: 0; }
.p-xs { padding: var(--space-xs); }
.p-sm { padding: var(--space-sm); }
.p-md { padding: var(--space-md); }
.p-lg { padding: var(--space-lg); }
.p-xl { padding: var(--space-xl); }

/* Shadow utilities */
.shadow-xs { box-shadow: var(--shadow-xs); }
.shadow-sm { box-shadow: var(--shadow-sm); }
.shadow-md { box-shadow: var(--shadow-md); }
.shadow-lg { box-shadow: var(--shadow-lg); }
.shadow-xl { box-shadow: var(--shadow-xl); }
.shadow-none { box-shadow: none; }

/* Border radius utilities */
.rounded-sm { border-radius: var(--radius-sm); }
.rounded-md { border-radius: var(--radius-md); }
.rounded-lg { border-radius: var(--radius-lg); }
.rounded-xl { border-radius: var(--radius-xl); }
.rounded-full { border-radius: var(--radius-full); }

/* ===== COMPONENT STYLES ===== */

/* Cards */
.card {
  background-color: var(--card-background);
  border: 1px solid var(--card-border);
  border-radius: var(--card-radius);
  box-shadow: var(--card-shadow);
  padding: var(--card-padding);
  transition: box-shadow var(--transition-base);
}

.card:hover {
  box-shadow: var(--shadow-md);
}

.card-header {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--space-md);
  padding-bottom: var(--space-sm);
  border-bottom: 1px solid var(--border-light);
}

.card-body {
  padding: var(--space-md) 0;
}

.card-footer {
  margin-top: var(--space-md);
  padding-top: var(--space-sm);
  border-top: 1px solid var(--border-light);
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--button-padding-y) var(--button-padding-x);
  font-size: var(--font-size-base);
  font-weight: var(--button-font-weight);
  line-height: 1.5;
  border-radius: var(--button-radius);
  border: 1px solid transparent;
  cursor: pointer;
  transition: all var(--transition-fast);
  text-decoration: none;
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  color: var(--text-inverse);
  background-color: var(--primary-600);
  border-color: var(--primary-600);
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--primary-700);
  border-color: var(--primary-700);
  box-shadow: var(--shadow-sm);
}

.btn-secondary {
  color: var(--text-inverse);
  background-color: var(--secondary-600);
  border-color: var(--secondary-600);
}

.btn-secondary:hover:not(:disabled) {
  background-color: var(--secondary-700);
  border-color: var(--secondary-700);
  box-shadow: var(--shadow-sm);
}

.btn-success {
  color: var(--text-inverse);
  background-color: var(--success);
  border-color: var(--success);
}

.btn-success:hover:not(:disabled) {
  background-color: var(--success-dark);
  border-color: var(--success-dark);
}

.btn-danger {
  color: var(--text-inverse);
  background-color: var(--danger);
  border-color: var(--danger);
}

.btn-danger:hover:not(:disabled) {
  background-color: var(--danger-dark);
  border-color: var(--danger-dark);
}

.btn-outline-primary {
  color: var(--primary-600);
  background-color: transparent;
  border-color: var(--primary-600);
}

.btn-outline-primary:hover:not(:disabled) {
  color: var(--text-inverse);
  background-color: var(--primary-600);
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: var(--font-size-sm);
}

.btn-lg {
  padding: 0.75rem 1.5rem;
  font-size: var(--font-size-lg);
}

/* Forms */
.form-group {
  margin-bottom: var(--space-md);
}

.form-label {
  display: block;
  margin-bottom: var(--space-xs);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
}

.form-control {
  display: block;
  width: 100%;
  height: var(--input-height);
  padding: 0 var(--input-padding-x);
  font-size: var(--font-size-base);
  line-height: 1.5;
  color: var(--text-primary);
  background-color: var(--input-background);
  border: 1px solid var(--input-border);
  border-radius: var(--input-radius);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.form-control:focus {
  outline: none;
  border-color: var(--input-border-focus);
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);
}

.form-control:disabled {
  background-color: var(--gray-100);
  opacity: 0.6;
  cursor: not-allowed;
}

.form-control.is-invalid {
  border-color: var(--border-error);
}

.form-control.is-invalid:focus {
  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.1);
}

.form-text {
  display: block;
  margin-top: var(--space-xs);
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
}

.invalid-feedback {
  display: block;
  margin-top: var(--space-xs);
  font-size: var(--font-size-sm);
  color: var(--danger);
}

/* Tables */
.table {
  width: 100%;
  border-collapse: collapse;
  background-color: var(--background-primary);
}

.table thead th {
  background-color: var(--table-header-bg);
  color: var(--table-header-text);
  font-weight: var(--font-weight-semibold);
  text-align: left;
  padding: var(--space-sm) var(--space-md);
  border-bottom: 2px solid var(--table-border);
}

.table tbody td {
  padding: var(--space-sm) var(--space-md);
  border-bottom: 1px solid var(--table-border);
}

.table tbody tr:hover {
  background-color: var(--table-hover);
}

.table-striped tbody tr:nth-child(odd) {
  background-color: var(--table-stripe);
}

.table-sm thead th,
.table-sm tbody td {
  padding: var(--space-xs) var(--space-sm);
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  border-radius: var(--radius-md);
  white-space: nowrap;
}

.badge-primary {
  color: var(--primary-900);
  background-color: var(--primary-100);
}

.badge-success {
  color: var(--success-dark);
  background-color: var(--success-light);
}

.badge-warning {
  color: var(--warning-dark);
  background-color: var(--warning-light);
}

.badge-danger {
  color: var(--danger-dark);
  background-color: var(--danger-light);
}

.badge-info {
  color: var(--info-dark);
  background-color: var(--info-light);
}

/* Privilege Level Badges */
.badge-privilege-dba {
  color: #ffffff;
  background-color: var(--privilege-dba);
}

.badge-privilege-elevated {
  color: #ffffff;
  background-color: var(--privilege-elevated);
}

.badge-privilege-developer {
  color: #3e2723;
  background-color: var(--privilege-developer);
}

.badge-privilege-business {
  color: #ffffff;
  background-color: var(--privilege-business);
}

.badge-privilege-readonly {
  color: #ffffff;
  background-color: var(--privilege-readonly);
}

.badge-privilege-limited {
  color: #ffffff;
  background-color: var(--privilege-limited);
}

.badge-privilege-none {
  color: #ffffff;
  background-color: var(--privilege-none);
}

/* Alerts */
.alert {
  padding: var(--space-md);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  margin-bottom: var(--space-md);
}

.alert-success {
  color: var(--success-dark);
  background-color: var(--success-light);
  border-color: var(--success);
}

.alert-warning {
  color: var(--warning-dark);
  background-color: var(--warning-light);
  border-color: var(--warning);
}

.alert-danger {
  color: var(--danger-dark);
  background-color: var(--danger-light);
  border-color: var(--danger);
}

.alert-info {
  color: var(--info-dark);
  background-color: var(--info-light);
  border-color: var(--info);
}

/* Modals */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: var(--z-modal-backdrop);
}

/* Bootstrap Modal Compatibility - .modal is the overlay, not the centered box */
.modal {
  /* Remove custom positioning that breaks Bootstrap modals */
  /* position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); */
  /* These properties are now inherited from Bootstrap */
}

/* Style the actual modal content box */
.modal-content {
  background-color: var(--background-primary);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-2xl);
}

.modal-dialog {
  max-width: 90vw;
  max-height: 90vh;
}

.modal-header {
  padding: var(--space-lg);
  border-bottom: 1px solid var(--border-light);
}

.modal-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
}

.modal-body {
  padding: var(--space-lg);
}

.modal-footer {
  padding: var(--space-lg);
  border-top: 1px solid var(--border-light);
  display: flex;
  gap: var(--space-sm);
  justify-content: flex-end;
}

/* Tooltips */
.tooltip {
  position: absolute;
  z-index: var(--z-tooltip);
  background-color: var(--gray-900);
  color: var(--text-inverse);
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  white-space: nowrap;
  box-shadow: var(--shadow-lg);
}

/* Loading spinners */
.spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid var(--border-light);
  border-top-color: var(--primary-600);
  border-radius: var(--radius-full);
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.spinner-lg {
  width: 2rem;
  height: 2rem;
  border-width: 3px;
}

/* Empty states */
.empty-state {
  text-align: center;
  padding: var(--space-3xl) var(--space-lg);
  color: var(--text-tertiary);
}

.empty-state-icon {
  font-size: var(--font-size-4xl);
  margin-bottom: var(--space-md);
  opacity: 0.5;
}

.empty-state-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  margin-bottom: var(--space-sm);
}

.empty-state-description {
  font-size: var(--font-size-base);
  max-width: 400px;
  margin: 0 auto;
}
`, "",{"version":3,"sources":["webpack://./src/theme.css"],"names":[],"mappings":"AAAA;;;;;;;;EAQE;;AAEF;EACE,+BAA+B;EAC/B,iDAAiD;EACjD,qBAAqB;EACrB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB,GAAG,qBAAqB;EAC9C,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;;EAEtB,iCAAiC;EACjC,mCAAmC;EACnC,uBAAuB;EACvB,wBAAwB;EACxB,wBAAwB;EACxB,wBAAwB;EACxB,wBAAwB;EACxB,wBAAwB,GAAG,0BAA0B;EACrD,wBAAwB;EACxB,wBAAwB;EACxB,wBAAwB;EACxB,wBAAwB;;EAExB,+BAA+B;EAC/B,6CAA6C;EAC7C,kBAAkB;EAClB,mBAAmB;EACnB,mBAAmB;EACnB,mBAAmB;EACnB,mBAAmB;EACnB,mBAAmB;EACnB,mBAAmB;EACnB,mBAAmB;EACnB,mBAAmB;EACnB,mBAAmB;;EAEnB,8BAA8B;EAC9B,sCAAsC;EACtC,wBAAwB;EACxB,kBAAkB;EAClB,uBAAuB;;EAEvB,wBAAwB;EACxB,kBAAkB;EAClB,uBAAuB;;EAEvB,uBAAuB;EACvB,iBAAiB;EACjB,sBAAsB;;EAEtB,qBAAqB;EACrB,eAAe;EACf,oBAAoB;;EAEpB,uCAAuC;EACvC,6CAA6C;EAC7C,wBAAwB,YAAY,cAAc;EAClD,6BAA6B,OAAO,sBAAsB;EAC1D,8BAA8B,MAAM,sBAAsB;EAC1D,6BAA6B,OAAO,qBAAqB;EACzD,6BAA6B,OAAO,qBAAqB;EACzD,4BAA4B,QAAQ,qBAAqB;EACzD,yBAAyB,WAAW,sBAAsB;;EAE1D,kCAAkC;EAClC,6BAA6B;EAC7B,+BAA+B;EAC/B,8BAA8B;EAC9B,2BAA2B;EAC3B,8BAA8B;EAC9B,0BAA0B;EAC1B,4BAA4B;;EAE5B,4BAA4B;EAC5B,uBAAuB;EACvB,yBAAyB;EACzB,wBAAwB;EACxB,wBAAwB;EACxB,uBAAuB;EACvB,oBAAoB;EACpB,0BAA0B;;EAE1B,8BAA8B;EAC9B,uBAAuB;EACvB,wBAAwB;EACxB,sBAAsB;EACtB,uBAAuB;EACvB,uBAAuB;;EAEvB,+BAA+B;EAC/B,mBAAmB,IAAI,QAAQ;EAC/B,kBAAkB,KAAK,QAAQ;EAC/B,gBAAgB,OAAO,SAAS;EAChC,kBAAkB,KAAK,SAAS;EAChC,gBAAgB,OAAO,SAAS;EAChC,iBAAiB,MAAM,SAAS;EAChC,iBAAiB,MAAM,SAAS;;EAEhC,2BAA2B;EAC3B;oGACkG;EAClG,qDAAqD;;EAErD,uBAAuB,MAAM,SAAS;EACtC,wBAAwB,KAAK,SAAS;EACtC,sBAAsB,OAAO,SAAS;EACtC,wBAAwB,KAAK,SAAS;EACtC,uBAAuB,MAAM,SAAS;EACtC,uBAAuB,MAAM,SAAS;EACtC,yBAAyB,IAAI,SAAS;EACtC,wBAAwB,KAAK,SAAS;;EAEtC,wBAAwB;EACxB,yBAAyB;EACzB,yBAAyB;EACzB,2BAA2B;EAC3B,uBAAuB;;EAEvB,yBAAyB;EACzB,yBAAyB;EACzB,2BAA2B;;EAE3B,8BAA8B;EAC9B,oBAAoB,KAAK,QAAQ;EACjC,qBAAqB,IAAI,QAAQ;EACjC,mBAAmB,MAAM,QAAQ;EACjC,oBAAoB,KAAK,SAAS;EAClC,kBAAkB,OAAO,SAAS;EAClC,qBAAqB,IAAI,aAAa;;EAEtC,wBAAwB;EACxB,4CAA4C;EAC5C,4EAA4E;EAC5E,kFAAkF;EAClF,oFAAoF;EACpF,sFAAsF;EACtF,mDAAmD;EACnD,qDAAqD;;EAErD,+BAA+B;EAC/B,kBAAkB;EAClB,gBAAgB;EAChB,eAAe;EACf,wBAAwB;EACxB,eAAe;EACf,iBAAiB;EACjB,iBAAiB;;EAEjB,4BAA4B;EAC5B,oCAAoC;EACpC,oCAAoC;EACpC,oCAAoC;;EAEpC,mCAAmC;EACnC,qBAAqB;EACrB,sBAAsB;EACtB,+BAA+B;EAC/B,qBAAqB;;EAErB,gBAAgB;EAChB,4CAA4C;EAC5C,kCAAkC;EAClC,+BAA+B;EAC/B,+BAA+B;EAC/B,+BAA+B;;EAE/B,kBAAkB;EAClB,0BAA0B;EAC1B,wBAAwB;EACxB,iCAAiC;EACjC,+CAA+C;;EAE/C,iBAAiB;EACjB,sBAAsB;EACtB,0BAA0B;EAC1B,mCAAmC;EACnC,yCAAyC;EACzC,gCAAgC;EAChC,6CAA6C;;EAE7C,iBAAiB;EACjB,mCAAmC;EACnC,8BAA8B;EAC9B,sCAAsC;EACtC,kCAAkC;EAClC,wCAAwC;AAC1C;;AAEA,kCAAkC;AAClC,sCAAsC;AACtC;EACE;IACE,6BAA6B;IAC7B,+BAA+B;IAC/B,8BAA8B;IAC9B,2BAA2B;IAC3B,8BAA8B;;IAE9B,uBAAuB;IACvB,yBAAyB;IACzB,wBAAwB;IACxB,wBAAwB;IACxB,uBAAuB;;IAEvB,uBAAuB;IACvB,wBAAwB;IACxB,sBAAsB;;IAEtB,0BAA0B;IAC1B,sBAAsB;;IAEtB,uBAAuB;IACvB,sBAAsB;IACtB,0BAA0B;EAC5B;AACF;;AAEA,8BAA8B;AAC9B;EACE,sBAAsB;EACtB,SAAS;EACT,UAAU;AACZ;;AAEA;EACE,oCAAoC;EACpC,gCAAgC;EAChC,sCAAsC;EACtC,0BAA0B;EAC1B,6CAA6C;EAC7C,mCAAmC;EACnC,kCAAkC;AACpC;;AAEA,gCAAgC;;AAEhC,mBAAmB;AACnB,gBAAgB,0BAA0B,EAAE;AAC5C,kBAAkB,4BAA4B,EAAE;AAChD,iBAAiB,2BAA2B,EAAE;AAC9C,gBAAgB,0BAA0B,EAAE;AAC5C,gBAAgB,qBAAqB,EAAE;AACvC,gBAAgB,qBAAqB,EAAE;AACvC,eAAe,oBAAoB,EAAE;AACrC,aAAa,kBAAkB,EAAE;;AAEjC,yBAAyB;AACzB,cAAc,2CAA2C,EAAE;AAC3D,gBAAgB,6CAA6C,EAAE;AAC/D,eAAe,4CAA4C,EAAE;AAC7D,oBAAoB,sCAAsC,EAAE;AAC5D,oBAAoB,sCAAsC,EAAE;AAC5D,mBAAmB,qCAAqC,EAAE;AAC1D,iBAAiB,mCAAmC,EAAE;;AAEtD,sBAAsB;AACtB,OAAO,SAAS,EAAE;AAClB,QAAQ,uBAAuB,EAAE;AACjC,QAAQ,uBAAuB,EAAE;AACjC,QAAQ,uBAAuB,EAAE;AACjC,QAAQ,uBAAuB,EAAE;AACjC,QAAQ,uBAAuB,EAAE;;AAEjC,QAAQ,aAAa,EAAE;AACvB,SAAS,2BAA2B,EAAE;AACtC,SAAS,2BAA2B,EAAE;AACtC,SAAS,2BAA2B,EAAE;AACtC,SAAS,2BAA2B,EAAE;AACtC,SAAS,2BAA2B,EAAE;;AAEtC,QAAQ,gBAAgB,EAAE;AAC1B,SAAS,8BAA8B,EAAE;AACzC,SAAS,8BAA8B,EAAE;AACzC,SAAS,8BAA8B,EAAE;AACzC,SAAS,8BAA8B,EAAE;AACzC,SAAS,8BAA8B,EAAE;;AAEzC,OAAO,UAAU,EAAE;AACnB,QAAQ,wBAAwB,EAAE;AAClC,QAAQ,wBAAwB,EAAE;AAClC,QAAQ,wBAAwB,EAAE;AAClC,QAAQ,wBAAwB,EAAE;AAClC,QAAQ,wBAAwB,EAAE;;AAElC,qBAAqB;AACrB,aAAa,4BAA4B,EAAE;AAC3C,aAAa,4BAA4B,EAAE;AAC3C,aAAa,4BAA4B,EAAE;AAC3C,aAAa,4BAA4B,EAAE;AAC3C,aAAa,4BAA4B,EAAE;AAC3C,eAAe,gBAAgB,EAAE;;AAEjC,4BAA4B;AAC5B,cAAc,+BAA+B,EAAE;AAC/C,cAAc,+BAA+B,EAAE;AAC/C,cAAc,+BAA+B,EAAE;AAC/C,cAAc,+BAA+B,EAAE;AAC/C,gBAAgB,iCAAiC,EAAE;;AAEnD,iCAAiC;;AAEjC,UAAU;AACV;EACE,wCAAwC;EACxC,oCAAoC;EACpC,iCAAiC;EACjC,8BAA8B;EAC9B,4BAA4B;EAC5B,6CAA6C;AAC/C;;AAEA;EACE,4BAA4B;AAC9B;;AAEA;EACE,8BAA8B;EAC9B,wCAAwC;EACxC,8BAA8B;EAC9B,+BAA+B;EAC/B,4CAA4C;AAC9C;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,2BAA2B;EAC3B,4BAA4B;EAC5B,yCAAyC;AAC3C;;AAEA,YAAY;AACZ;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,wDAAwD;EACxD,gCAAgC;EAChC,sCAAsC;EACtC,gBAAgB;EAChB,mCAAmC;EACnC,6BAA6B;EAC7B,eAAe;EACf,sCAAsC;EACtC,qBAAqB;EACrB,mBAAmB;AACrB;;AAEA;EACE,YAAY;EACZ,mBAAmB;AACrB;;AAEA;EACE,0BAA0B;EAC1B,oCAAoC;EACpC,gCAAgC;AAClC;;AAEA;EACE,oCAAoC;EACpC,gCAAgC;EAChC,4BAA4B;AAC9B;;AAEA;EACE,0BAA0B;EAC1B,sCAAsC;EACtC,kCAAkC;AACpC;;AAEA;EACE,sCAAsC;EACtC,kCAAkC;EAClC,4BAA4B;AAC9B;;AAEA;EACE,0BAA0B;EAC1B,gCAAgC;EAChC,4BAA4B;AAC9B;;AAEA;EACE,qCAAqC;EACrC,iCAAiC;AACnC;;AAEA;EACE,0BAA0B;EAC1B,+BAA+B;EAC/B,2BAA2B;AAC7B;;AAEA;EACE,oCAAoC;EACpC,gCAAgC;AAClC;;AAEA;EACE,yBAAyB;EACzB,6BAA6B;EAC7B,gCAAgC;AAClC;;AAEA;EACE,0BAA0B;EAC1B,oCAAoC;AACtC;;AAEA;EACE,yBAAyB;EACzB,8BAA8B;AAChC;;AAEA;EACE,uBAAuB;EACvB,8BAA8B;AAChC;;AAEA,UAAU;AACV;EACE,8BAA8B;AAChC;;AAEA;EACE,cAAc;EACd,8BAA8B;EAC9B,8BAA8B;EAC9B,sCAAsC;EACtC,0BAA0B;AAC5B;;AAEA;EACE,cAAc;EACd,WAAW;EACX,2BAA2B;EAC3B,iCAAiC;EACjC,gCAAgC;EAChC,gBAAgB;EAChB,0BAA0B;EAC1B,yCAAyC;EACzC,qCAAqC;EACrC,kCAAkC;EAClC,kFAAkF;AACpF;;AAEA;EACE,aAAa;EACb,uCAAuC;EACvC,6CAA6C;AAC/C;;AAEA;EACE,iCAAiC;EACjC,YAAY;EACZ,mBAAmB;AACrB;;AAEA;EACE,iCAAiC;AACnC;;AAEA;EACE,4CAA4C;AAC9C;;AAEA;EACE,cAAc;EACd,2BAA2B;EAC3B,8BAA8B;EAC9B,2BAA2B;AAC7B;;AAEA;EACE,cAAc;EACd,2BAA2B;EAC3B,8BAA8B;EAC9B,oBAAoB;AACtB;;AAEA,WAAW;AACX;EACE,WAAW;EACX,yBAAyB;EACzB,2CAA2C;AAC7C;;AAEA;EACE,wCAAwC;EACxC,+BAA+B;EAC/B,wCAAwC;EACxC,gBAAgB;EAChB,wCAAwC;EACxC,4CAA4C;AAC9C;;AAEA;EACE,wCAAwC;EACxC,4CAA4C;AAC9C;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,qCAAqC;AACvC;;AAEA;;EAEE,wCAAwC;AAC1C;;AAEA,WAAW;AACX;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,8BAA8B;EAC9B,wCAAwC;EACxC,cAAc;EACd,+BAA+B;EAC/B,mBAAmB;AACrB;;AAEA;EACE,yBAAyB;EACzB,oCAAoC;AACtC;;AAEA;EACE,0BAA0B;EAC1B,sCAAsC;AACxC;;AAEA;EACE,0BAA0B;EAC1B,sCAAsC;AACxC;;AAEA;EACE,yBAAyB;EACzB,qCAAqC;AACvC;;AAEA;EACE,uBAAuB;EACvB,mCAAmC;AACrC;;AAEA,2BAA2B;AAC3B;EACE,cAAc;EACd,sCAAsC;AACxC;;AAEA;EACE,cAAc;EACd,2CAA2C;AAC7C;;AAEA;EACE,cAAc;EACd,4CAA4C;AAC9C;;AAEA;EACE,cAAc;EACd,2CAA2C;AAC7C;;AAEA;EACE,cAAc;EACd,2CAA2C;AAC7C;;AAEA;EACE,cAAc;EACd,0CAA0C;AAC5C;;AAEA;EACE,cAAc;EACd,uCAAuC;AACzC;;AAEA,WAAW;AACX;EACE,wBAAwB;EACxB,6BAA6B;EAC7B,+BAA+B;EAC/B,8BAA8B;AAChC;;AAEA;EACE,0BAA0B;EAC1B,sCAAsC;EACtC,4BAA4B;AAC9B;;AAEA;EACE,0BAA0B;EAC1B,sCAAsC;EACtC,4BAA4B;AAC9B;;AAEA;EACE,yBAAyB;EACzB,qCAAqC;EACrC,2BAA2B;AAC7B;;AAEA;EACE,uBAAuB;EACvB,mCAAmC;EACnC,yBAAyB;AAC3B;;AAEA,WAAW;AACX;EACE,eAAe;EACf,MAAM;EACN,OAAO;EACP,WAAW;EACX,YAAY;EACZ,oCAAoC;EACpC,gCAAgC;AAClC;;AAEA,gFAAgF;AAChF;EACE,2DAA2D;EAC3D,4EAA4E;EAC5E,sDAAsD;AACxD;;AAEA,uCAAuC;AACvC;EACE,2CAA2C;EAC3C,+BAA+B;EAC/B,6BAA6B;AAC/B;;AAEA;EACE,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,wBAAwB;EACxB,4CAA4C;AAC9C;;AAEA;EACE,8BAA8B;EAC9B,wCAAwC;AAC1C;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,wBAAwB;EACxB,yCAAyC;EACzC,aAAa;EACb,oBAAoB;EACpB,yBAAyB;AAC3B;;AAEA,aAAa;AACb;EACE,kBAAkB;EAClB,yBAAyB;EACzB,iCAAiC;EACjC,0BAA0B;EAC1B,wCAAwC;EACxC,+BAA+B;EAC/B,8BAA8B;EAC9B,mBAAmB;EACnB,4BAA4B;AAC9B;;AAEA,qBAAqB;AACrB;EACE,qBAAqB;EACrB,WAAW;EACX,YAAY;EACZ,qCAAqC;EACrC,oCAAoC;EACpC,iCAAiC;EACjC,oCAAoC;AACtC;;AAEA;EACE,KAAK,yBAAyB,EAAE;AAClC;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,iBAAiB;AACnB;;AAEA,iBAAiB;AACjB;EACE,kBAAkB;EAClB,yCAAyC;EACzC,2BAA2B;AAC7B;;AAEA;EACE,+BAA+B;EAC/B,8BAA8B;EAC9B,YAAY;AACd;;AAEA;EACE,8BAA8B;EAC9B,wCAAwC;EACxC,4BAA4B;EAC5B,8BAA8B;AAChC;;AAEA;EACE,gCAAgC;EAChC,gBAAgB;EAChB,cAAc;AAChB","sourcesContent":["/**\n * UAR Professional Theme\n *\n * Enterprise-grade color palette and design system for the\n * Database Access Management & Attestation (UAR) compliance application.\n *\n * Based on professional compliance application standards with\n * emphasis on readability, accessibility, and trustworthiness.\n */\n\n:root {\n  /* ===== PRIMARY COLORS ===== */\n  /* Professional blues for trust and reliability */\n  --primary-50: #e3f2fd;\n  --primary-100: #bbdefb;\n  --primary-200: #90caf9;\n  --primary-300: #64b5f6;\n  --primary-400: #42a5f5;\n  --primary-500: #2196f3;  /* Main brand color */\n  --primary-600: #1e88e5;\n  --primary-700: #1976d2;\n  --primary-800: #1565c0;\n  --primary-900: #0d47a1;\n\n  /* ===== SECONDARY COLORS ===== */\n  /* Complementary teal for accents */\n  --secondary-50: #e0f2f1;\n  --secondary-100: #b2dfdb;\n  --secondary-200: #80cbc4;\n  --secondary-300: #4db6ac;\n  --secondary-400: #26a69a;\n  --secondary-500: #009688;  /* Secondary brand color */\n  --secondary-600: #00897b;\n  --secondary-700: #00796b;\n  --secondary-800: #00695c;\n  --secondary-900: #004d40;\n\n  /* ===== NEUTRAL COLORS ===== */\n  /* Grays for backgrounds, borders, and text */\n  --gray-50: #fafafa;\n  --gray-100: #f5f5f5;\n  --gray-200: #eeeeee;\n  --gray-300: #e0e0e0;\n  --gray-400: #bdbdbd;\n  --gray-500: #9e9e9e;\n  --gray-600: #757575;\n  --gray-700: #616161;\n  --gray-800: #424242;\n  --gray-900: #212121;\n\n  /* ===== STATUS COLORS ===== */\n  /* Semantic colors for user feedback */\n  --success-light: #c8e6c9;\n  --success: #4caf50;\n  --success-dark: #388e3c;\n\n  --warning-light: #fff9c4;\n  --warning: #ffc107;\n  --warning-dark: #f57f17;\n\n  --danger-light: #ffcdd2;\n  --danger: #f44336;\n  --danger-dark: #c62828;\n\n  --info-light: #b3e5fc;\n  --info: #03a9f4;\n  --info-dark: #0277bd;\n\n  /* ===== PRIVILEGE LEVEL COLORS ===== */\n  /* Specific to UAR privilege categorization */\n  --privilege-dba: #d32f2f;           /* DBA - Red */\n  --privilege-elevated: #f57c00;      /* Elevated - Orange */\n  --privilege-developer: #fbc02d;     /* Developer - Amber */\n  --privilege-business: #388e3c;      /* Business - Green */\n  --privilege-readonly: #1976d2;      /* Read-Only - Blue */\n  --privilege-limited: #7b1fa2;       /* Limited - Purple */\n  --privilege-none: #616161;          /* None/Login - Gray */\n\n  /* ===== BACKGROUND COLORS ===== */\n  --background-primary: #ffffff;\n  --background-secondary: #f8f9fa;\n  --background-tertiary: #f1f3f5;\n  --background-hover: #e9ecef;\n  --background-selected: #e3f2fd;\n  --background-dark: #263238;\n  --background-darker: #1a1f24;\n\n  /* ===== TEXT COLORS ===== */\n  --text-primary: #212121;\n  --text-secondary: #616161;\n  --text-tertiary: #9e9e9e;\n  --text-disabled: #bdbdbd;\n  --text-inverse: #ffffff;\n  --text-link: #1976d2;\n  --text-link-hover: #1565c0;\n\n  /* ===== BORDER COLORS ===== */\n  --border-light: #e0e0e0;\n  --border-medium: #bdbdbd;\n  --border-dark: #757575;\n  --border-focus: #2196f3;\n  --border-error: #f44336;\n\n  /* ===== SPACING SYSTEM ===== */\n  --space-xs: 0.25rem;   /* 4px */\n  --space-sm: 0.5rem;    /* 8px */\n  --space-md: 1rem;      /* 16px */\n  --space-lg: 1.5rem;    /* 24px */\n  --space-xl: 2rem;      /* 32px */\n  --space-2xl: 3rem;     /* 48px */\n  --space-3xl: 4rem;     /* 64px */\n\n  /* ===== TYPOGRAPHY ===== */\n  --font-family-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',\n                      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;\n  --font-family-mono: 'Courier New', Courier, monospace;\n\n  --font-size-xs: 0.75rem;     /* 12px */\n  --font-size-sm: 0.875rem;    /* 14px */\n  --font-size-base: 1rem;      /* 16px */\n  --font-size-lg: 1.125rem;    /* 18px */\n  --font-size-xl: 1.25rem;     /* 20px */\n  --font-size-2xl: 1.5rem;     /* 24px */\n  --font-size-3xl: 1.875rem;   /* 30px */\n  --font-size-4xl: 2.25rem;    /* 36px */\n\n  --font-weight-light: 300;\n  --font-weight-normal: 400;\n  --font-weight-medium: 500;\n  --font-weight-semibold: 600;\n  --font-weight-bold: 700;\n\n  --line-height-tight: 1.25;\n  --line-height-normal: 1.5;\n  --line-height-relaxed: 1.75;\n\n  /* ===== BORDER RADIUS ===== */\n  --radius-sm: 0.25rem;    /* 4px */\n  --radius-md: 0.375rem;   /* 6px */\n  --radius-lg: 0.5rem;     /* 8px */\n  --radius-xl: 0.75rem;    /* 12px */\n  --radius-2xl: 1rem;      /* 16px */\n  --radius-full: 9999px;   /* Circular */\n\n  /* ===== SHADOWS ===== */\n  --shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.05);\n  --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);\n  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);\n  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);\n  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);\n  --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);\n  --shadow-inner: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);\n\n  /* ===== Z-INDEX LAYERS ===== */\n  --z-dropdown: 1000;\n  --z-sticky: 1020;\n  --z-fixed: 1030;\n  --z-modal-backdrop: 1040;\n  --z-modal: 1050;\n  --z-popover: 1060;\n  --z-tooltip: 1070;\n\n  /* ===== TRANSITIONS ===== */\n  --transition-fast: 150ms ease-in-out;\n  --transition-base: 250ms ease-in-out;\n  --transition-slow: 350ms ease-in-out;\n\n  /* ===== COMPONENT-SPECIFIC ===== */\n  --header-height: 60px;\n  --sidebar-width: 250px;\n  --sidebar-collapsed-width: 60px;\n  --footer-height: 50px;\n\n  /* Card styles */\n  --card-background: var(--background-primary);\n  --card-border: var(--border-light);\n  --card-shadow: var(--shadow-sm);\n  --card-radius: var(--radius-lg);\n  --card-padding: var(--space-lg);\n\n  /* Button styles */\n  --button-padding-y: 0.5rem;\n  --button-padding-x: 1rem;\n  --button-radius: var(--radius-md);\n  --button-font-weight: var(--font-weight-medium);\n\n  /* Input styles */\n  --input-height: 2.5rem;\n  --input-padding-x: 0.75rem;\n  --input-border: var(--border-light);\n  --input-border-focus: var(--border-focus);\n  --input-radius: var(--radius-md);\n  --input-background: var(--background-primary);\n\n  /* Table styles */\n  --table-border: var(--border-light);\n  --table-stripe: var(--gray-50);\n  --table-hover: var(--background-hover);\n  --table-header-bg: var(--gray-100);\n  --table-header-text: var(--text-primary);\n}\n\n/* ===== DARK MODE SUPPORT ===== */\n/* (Optional - can be enabled later) */\n@media (prefers-color-scheme: dark) {\n  :root.dark-mode {\n    --background-primary: #1a1f24;\n    --background-secondary: #263238;\n    --background-tertiary: #2e3944;\n    --background-hover: #37424d;\n    --background-selected: #1565c0;\n\n    --text-primary: #e0e0e0;\n    --text-secondary: #b0b0b0;\n    --text-tertiary: #808080;\n    --text-disabled: #606060;\n    --text-inverse: #212121;\n\n    --border-light: #37424d;\n    --border-medium: #4a5660;\n    --border-dark: #616e7c;\n\n    --card-background: #263238;\n    --card-border: #37424d;\n\n    --table-stripe: #2e3944;\n    --table-hover: #37424d;\n    --table-header-bg: #2e3944;\n  }\n}\n\n/* ===== GLOBAL RESETS ===== */\n* {\n  box-sizing: border-box;\n  margin: 0;\n  padding: 0;\n}\n\nbody {\n  font-family: var(--font-family-sans);\n  font-size: var(--font-size-base);\n  line-height: var(--line-height-normal);\n  color: var(--text-primary);\n  background-color: var(--background-secondary);\n  -webkit-font-smoothing: antialiased;\n  -moz-osx-font-smoothing: grayscale;\n}\n\n/* ===== UTILITY CLASSES ===== */\n\n/* Text utilities */\n.text-primary { color: var(--text-primary); }\n.text-secondary { color: var(--text-secondary); }\n.text-tertiary { color: var(--text-tertiary); }\n.text-inverse { color: var(--text-inverse); }\n.text-success { color: var(--success); }\n.text-warning { color: var(--warning); }\n.text-danger { color: var(--danger); }\n.text-info { color: var(--info); }\n\n/* Background utilities */\n.bg-primary { background-color: var(--background-primary); }\n.bg-secondary { background-color: var(--background-secondary); }\n.bg-tertiary { background-color: var(--background-tertiary); }\n.bg-success-light { background-color: var(--success-light); }\n.bg-warning-light { background-color: var(--warning-light); }\n.bg-danger-light { background-color: var(--danger-light); }\n.bg-info-light { background-color: var(--info-light); }\n\n/* Spacing utilities */\n.m-0 { margin: 0; }\n.m-xs { margin: var(--space-xs); }\n.m-sm { margin: var(--space-sm); }\n.m-md { margin: var(--space-md); }\n.m-lg { margin: var(--space-lg); }\n.m-xl { margin: var(--space-xl); }\n\n.mt-0 { margin-top: 0; }\n.mt-xs { margin-top: var(--space-xs); }\n.mt-sm { margin-top: var(--space-sm); }\n.mt-md { margin-top: var(--space-md); }\n.mt-lg { margin-top: var(--space-lg); }\n.mt-xl { margin-top: var(--space-xl); }\n\n.mb-0 { margin-bottom: 0; }\n.mb-xs { margin-bottom: var(--space-xs); }\n.mb-sm { margin-bottom: var(--space-sm); }\n.mb-md { margin-bottom: var(--space-md); }\n.mb-lg { margin-bottom: var(--space-lg); }\n.mb-xl { margin-bottom: var(--space-xl); }\n\n.p-0 { padding: 0; }\n.p-xs { padding: var(--space-xs); }\n.p-sm { padding: var(--space-sm); }\n.p-md { padding: var(--space-md); }\n.p-lg { padding: var(--space-lg); }\n.p-xl { padding: var(--space-xl); }\n\n/* Shadow utilities */\n.shadow-xs { box-shadow: var(--shadow-xs); }\n.shadow-sm { box-shadow: var(--shadow-sm); }\n.shadow-md { box-shadow: var(--shadow-md); }\n.shadow-lg { box-shadow: var(--shadow-lg); }\n.shadow-xl { box-shadow: var(--shadow-xl); }\n.shadow-none { box-shadow: none; }\n\n/* Border radius utilities */\n.rounded-sm { border-radius: var(--radius-sm); }\n.rounded-md { border-radius: var(--radius-md); }\n.rounded-lg { border-radius: var(--radius-lg); }\n.rounded-xl { border-radius: var(--radius-xl); }\n.rounded-full { border-radius: var(--radius-full); }\n\n/* ===== COMPONENT STYLES ===== */\n\n/* Cards */\n.card {\n  background-color: var(--card-background);\n  border: 1px solid var(--card-border);\n  border-radius: var(--card-radius);\n  box-shadow: var(--card-shadow);\n  padding: var(--card-padding);\n  transition: box-shadow var(--transition-base);\n}\n\n.card:hover {\n  box-shadow: var(--shadow-md);\n}\n\n.card-header {\n  font-size: var(--font-size-lg);\n  font-weight: var(--font-weight-semibold);\n  margin-bottom: var(--space-md);\n  padding-bottom: var(--space-sm);\n  border-bottom: 1px solid var(--border-light);\n}\n\n.card-body {\n  padding: var(--space-md) 0;\n}\n\n.card-footer {\n  margin-top: var(--space-md);\n  padding-top: var(--space-sm);\n  border-top: 1px solid var(--border-light);\n}\n\n/* Buttons */\n.btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  padding: var(--button-padding-y) var(--button-padding-x);\n  font-size: var(--font-size-base);\n  font-weight: var(--button-font-weight);\n  line-height: 1.5;\n  border-radius: var(--button-radius);\n  border: 1px solid transparent;\n  cursor: pointer;\n  transition: all var(--transition-fast);\n  text-decoration: none;\n  white-space: nowrap;\n}\n\n.btn:disabled {\n  opacity: 0.6;\n  cursor: not-allowed;\n}\n\n.btn-primary {\n  color: var(--text-inverse);\n  background-color: var(--primary-600);\n  border-color: var(--primary-600);\n}\n\n.btn-primary:hover:not(:disabled) {\n  background-color: var(--primary-700);\n  border-color: var(--primary-700);\n  box-shadow: var(--shadow-sm);\n}\n\n.btn-secondary {\n  color: var(--text-inverse);\n  background-color: var(--secondary-600);\n  border-color: var(--secondary-600);\n}\n\n.btn-secondary:hover:not(:disabled) {\n  background-color: var(--secondary-700);\n  border-color: var(--secondary-700);\n  box-shadow: var(--shadow-sm);\n}\n\n.btn-success {\n  color: var(--text-inverse);\n  background-color: var(--success);\n  border-color: var(--success);\n}\n\n.btn-success:hover:not(:disabled) {\n  background-color: var(--success-dark);\n  border-color: var(--success-dark);\n}\n\n.btn-danger {\n  color: var(--text-inverse);\n  background-color: var(--danger);\n  border-color: var(--danger);\n}\n\n.btn-danger:hover:not(:disabled) {\n  background-color: var(--danger-dark);\n  border-color: var(--danger-dark);\n}\n\n.btn-outline-primary {\n  color: var(--primary-600);\n  background-color: transparent;\n  border-color: var(--primary-600);\n}\n\n.btn-outline-primary:hover:not(:disabled) {\n  color: var(--text-inverse);\n  background-color: var(--primary-600);\n}\n\n.btn-sm {\n  padding: 0.375rem 0.75rem;\n  font-size: var(--font-size-sm);\n}\n\n.btn-lg {\n  padding: 0.75rem 1.5rem;\n  font-size: var(--font-size-lg);\n}\n\n/* Forms */\n.form-group {\n  margin-bottom: var(--space-md);\n}\n\n.form-label {\n  display: block;\n  margin-bottom: var(--space-xs);\n  font-size: var(--font-size-sm);\n  font-weight: var(--font-weight-medium);\n  color: var(--text-primary);\n}\n\n.form-control {\n  display: block;\n  width: 100%;\n  height: var(--input-height);\n  padding: 0 var(--input-padding-x);\n  font-size: var(--font-size-base);\n  line-height: 1.5;\n  color: var(--text-primary);\n  background-color: var(--input-background);\n  border: 1px solid var(--input-border);\n  border-radius: var(--input-radius);\n  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);\n}\n\n.form-control:focus {\n  outline: none;\n  border-color: var(--input-border-focus);\n  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.1);\n}\n\n.form-control:disabled {\n  background-color: var(--gray-100);\n  opacity: 0.6;\n  cursor: not-allowed;\n}\n\n.form-control.is-invalid {\n  border-color: var(--border-error);\n}\n\n.form-control.is-invalid:focus {\n  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.1);\n}\n\n.form-text {\n  display: block;\n  margin-top: var(--space-xs);\n  font-size: var(--font-size-sm);\n  color: var(--text-tertiary);\n}\n\n.invalid-feedback {\n  display: block;\n  margin-top: var(--space-xs);\n  font-size: var(--font-size-sm);\n  color: var(--danger);\n}\n\n/* Tables */\n.table {\n  width: 100%;\n  border-collapse: collapse;\n  background-color: var(--background-primary);\n}\n\n.table thead th {\n  background-color: var(--table-header-bg);\n  color: var(--table-header-text);\n  font-weight: var(--font-weight-semibold);\n  text-align: left;\n  padding: var(--space-sm) var(--space-md);\n  border-bottom: 2px solid var(--table-border);\n}\n\n.table tbody td {\n  padding: var(--space-sm) var(--space-md);\n  border-bottom: 1px solid var(--table-border);\n}\n\n.table tbody tr:hover {\n  background-color: var(--table-hover);\n}\n\n.table-striped tbody tr:nth-child(odd) {\n  background-color: var(--table-stripe);\n}\n\n.table-sm thead th,\n.table-sm tbody td {\n  padding: var(--space-xs) var(--space-sm);\n}\n\n/* Badges */\n.badge {\n  display: inline-flex;\n  align-items: center;\n  padding: 0.25rem 0.5rem;\n  font-size: var(--font-size-xs);\n  font-weight: var(--font-weight-semibold);\n  line-height: 1;\n  border-radius: var(--radius-md);\n  white-space: nowrap;\n}\n\n.badge-primary {\n  color: var(--primary-900);\n  background-color: var(--primary-100);\n}\n\n.badge-success {\n  color: var(--success-dark);\n  background-color: var(--success-light);\n}\n\n.badge-warning {\n  color: var(--warning-dark);\n  background-color: var(--warning-light);\n}\n\n.badge-danger {\n  color: var(--danger-dark);\n  background-color: var(--danger-light);\n}\n\n.badge-info {\n  color: var(--info-dark);\n  background-color: var(--info-light);\n}\n\n/* Privilege Level Badges */\n.badge-privilege-dba {\n  color: #ffffff;\n  background-color: var(--privilege-dba);\n}\n\n.badge-privilege-elevated {\n  color: #ffffff;\n  background-color: var(--privilege-elevated);\n}\n\n.badge-privilege-developer {\n  color: #3e2723;\n  background-color: var(--privilege-developer);\n}\n\n.badge-privilege-business {\n  color: #ffffff;\n  background-color: var(--privilege-business);\n}\n\n.badge-privilege-readonly {\n  color: #ffffff;\n  background-color: var(--privilege-readonly);\n}\n\n.badge-privilege-limited {\n  color: #ffffff;\n  background-color: var(--privilege-limited);\n}\n\n.badge-privilege-none {\n  color: #ffffff;\n  background-color: var(--privilege-none);\n}\n\n/* Alerts */\n.alert {\n  padding: var(--space-md);\n  border: 1px solid transparent;\n  border-radius: var(--radius-md);\n  margin-bottom: var(--space-md);\n}\n\n.alert-success {\n  color: var(--success-dark);\n  background-color: var(--success-light);\n  border-color: var(--success);\n}\n\n.alert-warning {\n  color: var(--warning-dark);\n  background-color: var(--warning-light);\n  border-color: var(--warning);\n}\n\n.alert-danger {\n  color: var(--danger-dark);\n  background-color: var(--danger-light);\n  border-color: var(--danger);\n}\n\n.alert-info {\n  color: var(--info-dark);\n  background-color: var(--info-light);\n  border-color: var(--info);\n}\n\n/* Modals */\n.modal-backdrop {\n  position: fixed;\n  top: 0;\n  left: 0;\n  width: 100%;\n  height: 100%;\n  background-color: rgba(0, 0, 0, 0.5);\n  z-index: var(--z-modal-backdrop);\n}\n\n/* Bootstrap Modal Compatibility - .modal is the overlay, not the centered box */\n.modal {\n  /* Remove custom positioning that breaks Bootstrap modals */\n  /* position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); */\n  /* These properties are now inherited from Bootstrap */\n}\n\n/* Style the actual modal content box */\n.modal-content {\n  background-color: var(--background-primary);\n  border-radius: var(--radius-xl);\n  box-shadow: var(--shadow-2xl);\n}\n\n.modal-dialog {\n  max-width: 90vw;\n  max-height: 90vh;\n}\n\n.modal-header {\n  padding: var(--space-lg);\n  border-bottom: 1px solid var(--border-light);\n}\n\n.modal-title {\n  font-size: var(--font-size-xl);\n  font-weight: var(--font-weight-semibold);\n}\n\n.modal-body {\n  padding: var(--space-lg);\n}\n\n.modal-footer {\n  padding: var(--space-lg);\n  border-top: 1px solid var(--border-light);\n  display: flex;\n  gap: var(--space-sm);\n  justify-content: flex-end;\n}\n\n/* Tooltips */\n.tooltip {\n  position: absolute;\n  z-index: var(--z-tooltip);\n  background-color: var(--gray-900);\n  color: var(--text-inverse);\n  padding: var(--space-xs) var(--space-sm);\n  border-radius: var(--radius-sm);\n  font-size: var(--font-size-sm);\n  white-space: nowrap;\n  box-shadow: var(--shadow-lg);\n}\n\n/* Loading spinners */\n.spinner {\n  display: inline-block;\n  width: 1rem;\n  height: 1rem;\n  border: 2px solid var(--border-light);\n  border-top-color: var(--primary-600);\n  border-radius: var(--radius-full);\n  animation: spin 0.6s linear infinite;\n}\n\n@keyframes spin {\n  to { transform: rotate(360deg); }\n}\n\n.spinner-lg {\n  width: 2rem;\n  height: 2rem;\n  border-width: 3px;\n}\n\n/* Empty states */\n.empty-state {\n  text-align: center;\n  padding: var(--space-3xl) var(--space-lg);\n  color: var(--text-tertiary);\n}\n\n.empty-state-icon {\n  font-size: var(--font-size-4xl);\n  margin-bottom: var(--space-md);\n  opacity: 0.5;\n}\n\n.empty-state-title {\n  font-size: var(--font-size-xl);\n  font-weight: var(--font-weight-semibold);\n  color: var(--text-secondary);\n  margin-bottom: var(--space-sm);\n}\n\n.empty-state-description {\n  font-size: var(--font-size-base);\n  max-width: 400px;\n  margin: 0 auto;\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
